import { useCallback, useMemo, useState } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  NodeTypes,
  Node,
  Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { IVRNode } from './nodes/IVRNode';
import { defaultEdgeOptions } from './edges/edgeConfig';
import { ROLE_COLOURS } from './nodes/nodeConfig';
import { GraphData, JourneyNodeData, applyDagreLayout } from '../utils/buildGraph';

interface JourneyCanvasProps {
  graphData: GraphData;
  onNodeClick: (configId: string) => void;
}

const nodeTypes: NodeTypes = {
  ivrNode: IVRNode,
};

/**
 * Finds all descendant node IDs of a given node by traversing edges.
 */
function getDescendants(nodeId: string, allEdges: Edge[], collapsedSet: Set<string>): Set<string> {
  const descendants = new Set<string>();
  const queue = [nodeId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const children = allEdges
      .filter((e) => e.source === current)
      .map((e) => e.target);

    for (const child of children) {
      if (!descendants.has(child) && child !== nodeId) {
        descendants.add(child);
        // Only traverse further if the child itself is not collapsed
        if (!collapsedSet.has(child)) {
          queue.push(child);
        }
      }
    }
  }

  return descendants;
}

/**
 * React Flow canvas wrapper with Dagre-positioned nodes, zoom, pan, minimap.
 * Supports collapsible sub-trees (REQ-7).
 * BR-14: REQ-6, REQ-7 — Zoom, Pan, Minimap, Collapsible Sub-trees
 */
export function JourneyCanvas({ graphData, onNodeClick }: JourneyCanvasProps) {
  // Track which nodes are collapsed
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(() => {
    // Default: condition nodes (business-hours, holidays) start collapsed
    const initial = new Set<string>();
    for (const node of graphData.nodes) {
      const data = node.data as JourneyNodeData;
      if ((data.configType === 'business-hours' || data.configType === 'holidays') && (data.childCount ?? 0) > 0) {
        initial.add(node.id);
      }
    }
    return initial;
  });

  // Compute visible nodes and edges based on collapse state
  const { visibleNodes, visibleEdges } = useMemo(() => {
    // Collect all hidden node IDs (descendants of collapsed nodes)
    const hiddenIds = new Set<string>();
    for (const collapsedId of collapsedNodes) {
      const descendants = getDescendants(collapsedId, graphData.edges, collapsedNodes);
      for (const d of descendants) {
        hiddenIds.add(d);
      }
    }

    // Filter nodes — keep non-hidden, inject collapse state + toggle callback
    const visible = graphData.nodes
      .filter((n) => !hiddenIds.has(n.id))
      .map((n) => ({
        ...n,
        data: {
          ...n.data,
          isCollapsed: collapsedNodes.has(n.id),
          onToggleCollapse: () => {
            setCollapsedNodes((prev) => {
              const next = new Set(prev);
              if (next.has(n.id)) {
                next.delete(n.id);
              } else {
                next.add(n.id);
              }
              return next;
            });
          },
        },
      }));

    // Filter edges — keep only edges where both source and target are visible
    const visibleIds = new Set(visible.map((n) => n.id));
    const filteredEdges = graphData.edges.filter(
      (e) => visibleIds.has(e.source) && visibleIds.has(e.target)
    );

    // Re-apply Dagre layout to visible nodes
    const layouted = applyDagreLayout(visible, filteredEdges);
    return { visibleNodes: layouted.nodes, visibleEdges: layouted.edges };
  }, [graphData, collapsedNodes]);

  const [nodes, setNodes, onNodesChange] = useNodesState(visibleNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(visibleEdges);

  // Update nodes/edges when visible set changes
  useMemo(() => {
    setNodes(visibleNodes);
    setEdges(visibleEdges);
  }, [visibleNodes, visibleEdges, setNodes, setEdges]);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      onNodeClick(node.id);
    },
    [onNodeClick]
  );

  return (
    <div style={{ width: '100%', height: '600px', border: '1px solid #E2E8F0', borderRadius: 8 }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        maxZoom={2}
        attributionPosition="bottom-left"
      >
        <Controls position="bottom-left" />
        <MiniMap
          nodeColor={(node) => {
            const data = node.data as JourneyNodeData;
            return ROLE_COLOURS[data?.role] ?? '#CBD5E1';
          }}
          nodeStrokeWidth={0}
          maskColor="rgba(240,242,245,0.7)"
          style={{
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            borderRadius: 8,
          }}
          position="bottom-right"
        />
        <Background color="#F1F5F9" gap={16} />
      </ReactFlow>
    </div>
  );
}
